import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueries, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, X, AlertTriangle } from 'lucide-react';
import { ordersApi, type OrderStatusPayload, type UserOrderListItem } from '../services/ordersApi';
import { myOrdersQueryKey, myOrderStatusQueryKey } from '../queries/myOrders';
import { menuQueryOptions } from '../queries/menu';
import { isTerminalOrderStatus, orderStatusLabel, isCancellableStatus } from '../utils/orderStatusLabels';
import { useUserStore } from '../store/userStore';

const BANNER_MAX_AGE_MS = 2 * 60 * 1000;

function bannerHiddenKey(token: string) {
  return `theory_banner_hidden:${token}`;
}

type OrderLine = { productId: string; amount: number; price: number; comment?: string };

function parseOrderItems(items: unknown): OrderLine[] {
  if (!Array.isArray(items)) return [];
  return items
    .filter((x): x is Record<string, unknown> => x != null && typeof x === 'object')
    .map(x => ({
      productId: String(x.productId ?? ''),
      amount: Number(x.amount ?? 1) || 1,
      price: Number(x.price ?? 0) || 0,
      comment: typeof x.comment === 'string' ? x.comment : undefined,
    }))
    .filter(x => x.productId);
}

export function OrderDetailSheet({
  order,
  token,
  onClose,
}: {
  order: UserOrderListItem;
  token: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const { data: menuCategories = [] } = useQuery(menuQueryOptions());

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of menuCategories) {
      for (const it of c.items) m.set(it.id, it.name);
    }
    return m;
  }, [menuCategories]);

  const statusQk = myOrderStatusQueryKey(token, order.id);
  const { data: statusPayload, isPending, isError } = useQuery({
    queryKey: statusQk,
    queryFn: () => ordersApi.getMyOrderStatus(token, order.id),
    staleTime: 10_000,
    retry: 2,
  });

  useEffect(() => {
    const url = ordersApi.getMyOrderStatusStreamUrl(order.id, token);
    const es = new EventSource(url);
    es.onmessage = e => {
      try {
        const data = JSON.parse(e.data) as { success?: boolean; status?: OrderStatusPayload };
        if (data.success && data.status) {
          queryClient.setQueryData(statusQk, data.status);
          if (isTerminalOrderStatus(data.status.status)) es.close();
        }
      } catch {
        /* ignore */
      }
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, [order.id, token, queryClient, statusQk]);

  const lines = parseOrderItems(order.items);
  const numStr = String(statusPayload?.orderNumber ?? order.orderNumber).padStart(6, '0');
  const code = statusPayload?.status;
  const statusLabel =
    code && !isError ? orderStatusLabel(code) : isPending ? 'Обновляем статус…' : 'Заказ в работе';
  const canCancel = !!code && isCancellableStatus(code) && order.paymentStatus !== 'cancelled';

  const handleCancel = async () => {
    if (!cancelConfirm) { setCancelConfirm(true); return; }
    setCancelling(true);
    setCancelError(null);
    try {
      await ordersApi.cancelOrder(token, order.id);
      await queryClient.invalidateQueries({ queryKey: myOrdersQueryKey(token) });
      await queryClient.invalidateQueries({ queryKey: myOrderStatusQueryKey(token, order.id) });
      onClose();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'Ошибка при отмене');
      setCancelConfirm(false);
    } finally {
      setCancelling(false);
    }
  };
  const when = new Date(order.createdAt).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <motion.div
      className="fixed inset-0 z-[100]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <button
        type="button"
        aria-label="Закрыть"
        className="absolute inset-0 w-full h-full border-0 cursor-default"
        style={{ background: 'rgba(0,0,0,0.45)' }}
        onClick={onClose}
      />
      <motion.div
        className="absolute left-0 right-0 bottom-0 mx-auto w-full max-w-md flex flex-col rounded-t-3xl overflow-hidden shadow-2xl"
        style={{ background: '#fff', maxHeight: '88vh' }}
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        onClick={e => e.stopPropagation()}
      >
        <div className="w-10 h-1 rounded-full bg-black/10 mx-auto mt-3 shrink-0" />
        <div className="px-5 pt-5 pb-2 overflow-y-auto flex-1 min-h-0">
          <p className="text-center text-xs font-medium text-black/40 uppercase tracking-wide mb-1">
            Номер заказа
          </p>
          <p className="text-center text-5xl font-bold tracking-tight text-[#0a0a0a] mb-5">№ {numStr}</p>

          <p className="text-xs font-medium text-black/40 mb-1">Статус</p>
          <p className="text-lg font-semibold text-[#0a0a0a] mb-4">{statusLabel}</p>

          <p className="text-xs font-medium text-black/40 mb-1">Время заказа</p>
          <p className="text-sm font-medium text-black/80 mb-5">{when}</p>

          <p className="text-xs font-medium text-black/40 mb-2">Состав</p>
          <ul className="space-y-3">
            {lines.map((line, i) => (
              <li
                key={`${line.productId}-${i}`}
                className="flex justify-between gap-3 text-sm border-b border-black/5 pb-3 last:border-0"
              >
                <div className="min-w-0">
                  <p className="font-medium text-[#1a1a1a]">{nameById.get(line.productId) ?? 'Блюдо'}</p>
                  {line.comment ? (
                    <p className="text-xs text-black/40 mt-0.5">{line.comment}</p>
                  ) : null}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-black/50">×{line.amount}</p>
                  <p className="font-semibold text-[#1a1a1a]">
                    {(line.price * line.amount).toLocaleString('ru')} ₽
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </div>
        <div
          className="shrink-0 px-5 pt-3 border-t border-black/5 space-y-2"
          style={{ paddingBottom: 'max(1.25rem, env(safe-area-inset-bottom))' }}
        >
          {cancelError && (
            <div className="flex items-center gap-2 text-xs text-red-600 bg-red-50 rounded-xl px-3 py-2">
              <AlertTriangle size={14} className="shrink-0" />
              {cancelError}
            </div>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="w-full py-3.5 rounded-2xl text-base font-semibold active:scale-[0.99] transition-transform disabled:opacity-50"
              style={{ background: '#fee2e2', color: '#dc2626' }}
            >
              {cancelling ? 'Отменяем…' : cancelConfirm ? 'Подтвердить отмену' : 'Отменить заказ'}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="w-full py-3.5 rounded-2xl text-base font-semibold text-white active:scale-[0.99] transition-transform"
            style={{ background: '#0a0a0a' }}
          >
            Закрыть
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export function ActiveOrderBanner() {
  const token = useUserStore(s => s.token);
  const [detailOrder, setDetailOrder] = useState<UserOrderListItem | null>(null);
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!token) {
      setHiddenIds(new Set());
      return;
    }
    try {
      const raw = sessionStorage.getItem(bannerHiddenKey(token));
      const arr = raw ? (JSON.parse(raw) as unknown) : [];
      setHiddenIds(new Set(Array.isArray(arr) ? arr : []));
    } catch {
      setHiddenIds(new Set());
    }
  }, [token]);

  const { data: orders = [] } = useQuery({
    queryKey: myOrdersQueryKey(token),
    queryFn: () => ordersApi.getMyOrders(token!),
    enabled: !!token,
    staleTime: 30_000,
  });

  const candidates = useMemo(() => {
    const now = Date.now();
    return orders
      .filter(o => {
        const t = new Date(o.createdAt).getTime();
        return Number.isFinite(t) && now - t <= BANNER_MAX_AGE_MS;
      })
      .filter(o => !hiddenIds.has(o.id))
      .slice(0, 20);
  }, [orders, hiddenIds]);

  const statusQueries = useQueries({
    queries: candidates.map(o => ({
      queryKey: myOrderStatusQueryKey(token, o.id),
      queryFn: () => ordersApi.getMyOrderStatus(token!, o.id),
      enabled: !!token && !!o.id,
      staleTime: 10_000,
      retry: 2,
      refetchInterval: (q: { state: { data?: { status?: string } | null } }) => {
        const s = q.state.data?.status;
        return s && !isTerminalOrderStatus(s) ? 12_000 : false;
      },
    })),
  });

  const activeOrders = useMemo(() => {
    return candidates.map((order, i) => {
      const q = statusQueries[i];
      return {
        order,
        statusPayload: q?.data ?? null,
        statusPending: q?.isPending ?? false,
        statusError: q?.isError ?? false,
      };
    });
  }, [candidates, statusQueries]);

  if (!token || activeOrders.length === 0) return null;

  const multi = activeOrders.length > 1;

  return (
    <>
      <div className="pb-3">
        <div
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-1 scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {activeOrders.map(({ order, statusPayload, statusPending, statusError }) => {
            const numStr = String(statusPayload?.orderNumber ?? order.orderNumber).padStart(6, '0');
            const code = statusPayload?.status;
            const label =
              code && !statusError
                ? orderStatusLabel(code)
                : statusPending
                  ? 'Обновляем статус…'
                  : 'Заказ в работе';
            const sub =
              !statusError && statusPayload?.waitingTime != null
                ? `≈ ${statusPayload.waitingTime} мин`
                : !statusError && statusPayload?.estimatedTime != null
                  ? String(statusPayload.estimatedTime)
                  : undefined;

            const showDismiss = !!code && !statusError && isTerminalOrderStatus(code);

            return (
              <div
                key={order.id}
                className={`snap-center shrink-0 flex items-stretch rounded-2xl overflow-hidden ${
                  multi ? 'w-[min(17.5rem,calc(100vw-3.25rem))]' : 'w-full'
                }`}
                style={{
                  background: '#0a0a0a',
                  color: '#fff',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                }}
              >
                <button
                  type="button"
                  onClick={() => setDetailOrder(order)}
                  className="flex flex-1 min-w-0 items-center gap-3 px-4 py-3 text-left transition-transform active:scale-[0.99]"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(255,255,255,0.12)' }}
                  >
                    <Package size={20} className="opacity-90" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium opacity-60">Заказ № {numStr}</p>
                    <p className="text-sm font-semibold truncate">{label}</p>
                    {sub ? <p className="text-xs opacity-50 mt-0.5">{sub}</p> : null}
                  </div>
                </button>
                {showDismiss ? (
                  <button
                    type="button"
                    aria-label="Скрыть"
                    className="shrink-0 w-11 flex items-center justify-center border-0 relative z-10 touch-manipulation"
                    style={{
                      background: 'rgba(255,255,255,0.08)',
                      color: '#fff',
                    }}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setHiddenIds(prev => {
                        const next = new Set(prev).add(order.id);
                        if (token) {
                          try {
                            sessionStorage.setItem(bannerHiddenKey(token), JSON.stringify(Array.from(next)));
                          } catch {
                            /* ignore */
                          }
                        }
                        return next;
                      });
                      setDetailOrder(prev => (prev?.id === order.id ? null : prev));
                    }}
                  >
                    <X size={20} strokeWidth={2} className="opacity-90 pointer-events-none" />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>

      <AnimatePresence>
        {detailOrder && token ? (
          <OrderDetailSheet
            key={detailOrder.id}
            order={detailOrder}
            token={token}
            onClose={() => setDetailOrder(null)}
          />
        ) : null}
      </AnimatePresence>
    </>
  );
}
